import { LinkField } from '@sitecore-jss/sitecore-jss-react';
import Button from 'components/helpers/Button';
import { Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { setShippingMethod, useCheckout } from 'components/helpers/Cart/CheckoutHelper';
import { useEffect, useRef, useState } from 'react';
import { classnames } from 'tailwindcss-classnames';
import useDictionary from 'lib/sitecore/use-dictionary';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { getEnumValue } from 'lib/util/enum';
import AlertSuccess from 'components/helpers/SvgIcon/icons/alert-success';
import InputError from 'components/helpers/SvgIcon/icons/input-error';
import { useCart } from 'components/helpers/Cart/CartHelper';
import {
  ProductItemRP,
  ShippingInformationDataLayerObject,
  gtmEcommerceDatalayerPush,
  alwaysSuppressLinkDataLayerPush,
} from 'lib/util/gtm-datalayer';

type ShippingMethodProps = {
  updateSteps: (value: number) => void;
  continueToReviewCTA: LinkField;
  shippingMethodLabel: Field<string>;
  shippingAddressLabel: Field<string>;
  reviewLabel: Field<string>;
  fedExRushSelector: Sitecore.Override.ItemEx[];
  shippingMethodSelector: Sitecore.Override.ItemEx[];
};

type ShippingFormProps = {
  updateSteps: (value: number) => void;
  continueToReviewCTA: LinkField;
  shippingMethodLabel: Field<string>;
  fedExRushSelector: Sitecore.Override.ItemEx[];
  shippingMethodSelector: Sitecore.Override.ItemEx[];
};

type ShippingHeadingSectionProps = {
  shippingMethodLabel: Field<string>;
};

const textSolidBlack = 'text-solidBlack';
const fontMedium = 'font-medium';
const maxWidth88px = 'max-w-[88px]';

const ErrorWarning = () => {
  const dictionary = useDictionary();
  return (
    <>
      <div className={classnames('flex', 'pt-2')}>
        <InputError />
        <span className="pl-2 text-sm text-red-destructive-shade">
          {dictionary.checkout_validation_fedex}
        </span>
      </div>
    </>
  );
};

const ShippingHeadingSection = ({
  shippingMethodLabel,
}: ShippingHeadingSectionProps): JSX.Element => {
  const dictionary = useDictionary();
  return (
    <>
      <legend>
        <Text
          field={shippingMethodLabel}
          tag="h2"
          className={classnames('text-body', textSolidBlack, fontMedium, 'md:text-2xl')}
        />
      </legend>
      <div className={classnames('pt-2', 'italic')}>{dictionary.checkout_validation_message}</div>
    </>
  );
};

const ShippingFormSection = ({
  continueToReviewCTA,
  updateSteps,
  shippingMethodLabel,
  fedExRushSelector,
  shippingMethodSelector,
}: ShippingFormProps): JSX.Element => {
  const { checkoutData } = useCheckout();
  const prevShippingMethodData = checkoutData.checkOutData.shippingMethod;
  const [fedexNumber, setFedexValues] = useState({
    firstValue: '',
    secondValue: '',
    thirdValue: '',
  });
  const [selectedShippingOption, setSelectedShippingOption] = useState('');
  const [selectedFedexOption, setSelectedFedexOption] = useState('');
  const [showFedex, setShowFedex] = useState(false);
  const [fedexNumberError, setFedexNumberError] = useState(false);
  const fedExRush = 'FedEx Rush';

  useEffect(() => {
    const updateFedexValues = (value: string) => {
      const length = value.length;
      if (length === 9) {
        setFedexValues({
          firstValue: value.slice(0, 4),
          secondValue: value.slice(4, 8),
          thirdValue: value.slice(8, 9),
        });
      } else {
        setFedexValues({ firstValue: '', secondValue: '', thirdValue: '' });
      }
    };
    const updateSelectedOptions = (data: {
      shippingMethod: string;
      fedExNumber?: string;
      deliverySelection: string;
    }) => {
      setSelectedShippingOption(data.shippingMethod || getEnumValue(shippingMethodSelector[0]));
      setSelectedFedexOption(data.deliverySelection || getEnumValue(fedExRushSelector[0]));
      setShowFedex(data.shippingMethod.includes(fedExRush));
    };
    updateFedexValues(prevShippingMethodData.fedExNumber);
    updateSelectedOptions(prevShippingMethodData);
  }, [fedExRushSelector, prevShippingMethodData, shippingMethodSelector]);
  const handleShippingOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.includes(fedExRush)) {
      setShowFedex(true);
    } else {
      setShowFedex(false);
    }
    setSelectedShippingOption(event.target.value);
  };
  const handleFedexOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFedexOption(event.target.value);
  };
  const firstInputRef = useRef<HTMLInputElement>(null);
  const secondInputRef = useRef<HTMLInputElement>(null);
  const thirdInputRef = useRef<HTMLInputElement>(null);
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^\d$/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete') {
      event.preventDefault();
    }
  };
  const handleFirstChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= 4) {
      setFedexValues({ ...fedexNumber, firstValue: value });
    }
    if (value.length === 4) {
      if (secondInputRef.current) {
        secondInputRef.current.focus();
      }
    }
  };
  const handleSecondChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= 4) {
      setFedexValues({ ...fedexNumber, secondValue: value });
    }
    if (value.length === 4) {
      if (thirdInputRef.current) {
        thirdInputRef.current.focus();
      }
    }
  };
  const handleThirdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= 1) {
      setFedexValues({ ...fedexNumber, thirdValue: value });
    }
  };

  const { fetchCart } = useCart();
  const pushShippingMethodDataLayerObject = () => {
    const items = fetchCart().map((item, i) => {
      return {
        item_id: item.itemNumber ?? item.id,
        item_name: item.name,
        affiliation: 'N/A',
        coupon: 'N/A',
        discount: 'N/A',
        index: i.toString(),
        item_brand: 'NMDP',
        item_list_name: 'order materials',
        item_variant: 'N/A',
        price: item.price,
        quantity: item.quantity,
      };
    }) as ProductItemRP[];

    const gmtData: ShippingInformationDataLayerObject = {
      event: 'add_shipping_info',
      ecommerce: {
        value: '0',
        currency: 'USD',
        coupon: 'N/A',
        shipping_tier: selectedShippingOption,
        items: items,
      },
    };
    gtmEcommerceDatalayerPush(gmtData);
  };

  const handleClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      (selectedShippingOption.includes(fedExRush) && fedexNumber.firstValue.length !== 4) ||
      (selectedShippingOption.includes(fedExRush) && fedexNumber.secondValue.length !== 4) ||
      (selectedShippingOption.includes(fedExRush) && fedexNumber.thirdValue.length !== 1)
    ) {
      setFedexNumberError(true);
      return;
    }
    setShippingMethod({
      shippingMethod: selectedShippingOption,
      fedExNumber: selectedShippingOption.includes(fedExRush)
        ? `${fedexNumber.firstValue}${fedexNumber.secondValue}${fedexNumber.thirdValue}`
        : '',
      deliverySelection: selectedShippingOption.includes(fedExRush) ? selectedFedexOption : '',
    });
    pushShippingMethodDataLayerObject();
    updateSteps(2);
  };
  const handleBlur = () => {
    const { firstValue, secondValue, thirdValue } = fedexNumber;
    const hasError = firstValue.length !== 4 || secondValue.length !== 4 || thirdValue.length !== 1;
    setFedexNumberError(hasError);
  };
  return (
    <form>
      <ShippingHeadingSection shippingMethodLabel={shippingMethodLabel} />
      <fieldset className={classnames('pt-6')}>
        {shippingMethodSelector.map((option) => (
          <div key={option.id} className={classnames('flex', 'justify-items-start', 'pb-4')}>
            <input
              type="radio"
              id={option.id}
              value={getEnumValue(option)}
              checked={selectedShippingOption === getEnumValue(option)}
              onChange={handleShippingOptionChange}
            />
            <label htmlFor={option.id}> {getEnumValue(option)} </label>
          </div>
        ))}
      </fieldset>
      {showFedex && (
        <div>
          <fieldset>
            <legend className={classnames(fontMedium, textSolidBlack, 'pb-2')}>FedEx #</legend>
            <input
              type="number"
              ref={firstInputRef}
              value={fedexNumber.firstValue}
              onChange={handleFirstChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              className={classnames(maxWidth88px, { error: fedexNumberError })}
              maxLength={4}
            />
            -
            <input
              type="number"
              ref={secondInputRef}
              value={fedexNumber.secondValue}
              onChange={handleSecondChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              className={classnames(maxWidth88px, { error: fedexNumberError })}
              maxLength={4}
            />
            -
            <input
              type="number"
              ref={thirdInputRef}
              value={fedexNumber.thirdValue}
              onChange={handleThirdChange}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              className={classnames(maxWidth88px, { error: fedexNumberError })}
              maxLength={1}
            />
            {fedexNumberError && <ErrorWarning />}
          </fieldset>
          <fieldset>
            <legend className={classnames(fontMedium, textSolidBlack, 'pb-2', 'pt-4')}>
              Delivery
            </legend>
            {fedExRushSelector.map((option) => (
              <div key={option.id} className={classnames('flex', 'justify-items-start', 'pb-4')}>
                <input
                  type="radio"
                  id={option.id}
                  value={getEnumValue(option)}
                  checked={selectedFedexOption === getEnumValue(option)}
                  onChange={handleFedexOptionChange}
                />
                <label htmlFor={option.id}> {getEnumValue(option)} </label>
              </div>
            ))}
          </fieldset>
        </div>
      )}
      <Button
        className={classnames('mt-4', 'w-full', 'text-center', 'md:w-auto')}
        linkField={continueToReviewCTA}
        onGtmPush={alwaysSuppressLinkDataLayerPush}
        onClick={handleClick}
      />
    </form>
  );
};

const ShippingMethod = ({
  continueToReviewCTA,
  updateSteps,
  shippingAddressLabel,
  shippingMethodLabel,
  reviewLabel,
  fedExRushSelector,
  shippingMethodSelector,
}: ShippingMethodProps): JSX.Element => {
  return (
    <>
      <div className={classnames('checkout-form', 'justify-center', 'w-full')}>
        <div
          className={classnames('progress-title', 'complete', 'pb-8', textSolidBlack, 'md:hidden')}
        >
          <AlertSuccess />
          {shippingAddressLabel.value}
        </div>
        <ShippingFormSection
          shippingMethodLabel={shippingMethodLabel}
          continueToReviewCTA={continueToReviewCTA}
          updateSteps={updateSteps}
          fedExRushSelector={fedExRushSelector}
          shippingMethodSelector={shippingMethodSelector}
        />
        <div className={classnames('progress-title', 'inactive', 'pt-8', 'leading-1', 'md:hidden')}>
          {reviewLabel.value}
        </div>
      </div>
    </>
  );
};

export default ShippingMethod;
